import * as React from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { cva } from "class-variance-authority";

import { cn } from "../utils";

const tabsVariants = cva("qodo qodo-tabs flex flex-col w-full", {
  variants: {
    triggerVariant: {
      default: "gap-2",
      button: "gap-4",
    },
  },
  defaultVariants: {
    triggerVariant: "default",
  },
});

const tabsListVariants = cva("inline-flex w-full items-end justify-start", {
  variants: {
    triggerVariant: {
      default: "gap-4",
      button: "gap-2",
    },
  },
  defaultVariants: {
    triggerVariant: "default",
  },
});

const tabsTriggerVariants = cva(
  "inline-flex items-center justify-center gap-1.5 font-normal whitespace-nowrap focus-visible:ring-ring/50 focus-visible:outline-ring focus-visible:ring-[3px] focus-visible:outline-1 disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default:
          "relative text-sm border-b border-transparent py-4 -mb-px tracking-[0.01em] leading-[150%] text-text-interactive-neutral-idle hover:text-text-interactive-neutral-hover data-[state=active]:text-text-interactive-emphasis-active data-[state=active]:border-purple-400 transition-[color,border-color]",
        button:
          "rounded-md px-3 py-[6px] text-sm border border-border-action-neutral-idle text-text-action-neutral-idle data-[state=active]:text-text-action-emphasis-idle data-[state=active]:border-border-bg-emphasis transition-colors",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

type TabsTriggerVariant = "default" | "button";
type TabsContextValue = {
  triggerVariant: TabsTriggerVariant;
};
const TabsContext = React.createContext<TabsContextValue>({
  triggerVariant: "default",
});

const Tabs = React.forwardRef<
  React.ComponentRef<typeof TabsPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root> & {
    triggerVariant?: TabsTriggerVariant;
  }
>(({ className, triggerVariant = "default", ...props }, ref) => {
  return (
    <TabsContext.Provider value={{ triggerVariant }}>
      <TabsPrimitive.Root
        ref={ref}
        data-slot="tabs"
        className={tabsVariants({ triggerVariant, className })}
        {...props}
      />
    </TabsContext.Provider>
  );
});
Tabs.displayName = "Tabs";

function TabsList({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List>) {
  const { triggerVariant } = React.useContext(TabsContext);
  return (
    <TabsPrimitive.List
      data-slot="tabs-list"
      className={tabsListVariants({ triggerVariant, className })}
      {...props}
    />
  );
}

function TabsTrigger({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  const { triggerVariant } = React.useContext(TabsContext);
  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={tabsTriggerVariants({ variant: triggerVariant, className })}
      {...props}
    />
  );
}

function TabsContent({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={cn("flex-1 outline-none", className)}
      {...props}
    />
  );
}

export { Tabs, TabsList, TabsTrigger, TabsContent };
